<script setup lang="ts">
import { useValidation, type ValidationRule } from '@/shared/composables/use-validation'
import { toKebabCase } from '@/shared/utils/format'
import type { Validate } from '@/shared/types/validation.types'

import BaseTextInput from '@/shared/components/input/BaseTextInput.vue'
import SkeletonLoader from '../loader/SkeletonLoader.vue'

interface Props {
  label: string
  icon?: string
  type?: ValidationRule
  required?: boolean
  max?: number
  readonly?: boolean
  disabled?: boolean
  loading?: boolean
  saving?: boolean
}

interface Emits {
  (e: 'input'): void
  (e: 'clear'): void
  (e: 'validate', validation: Validate): void
}

defineOptions({ inheritAttrs: false })
const props = defineProps<Props>()
const emits = defineEmits<Emits>()
const model = defineModel<string>()
const name = toKebabCase(props.label)

const { validation, markDirty } = useValidation(
  model,
  props.type,
  props.required,
  props.max,
  (validation) => emits('validate', validation),
)
</script>

<template>
  <div v-if="loading" class="w-full">
    <SkeletonLoader class="h-3.5 w-1/2 mb-1.5 mt-1" />
    <SkeletonLoader class="h-9.5 w-full" />
  </div>
  <div v-else class="relative w-full flex items-center">
    <BaseTextInput
      :id="name"
      :name="name"
      :label="label"
      v-model.trim="model"
      :required="required"
      :readonly="readonly"
      :disabled="disabled"
      :validate="validation"
      :saving="saving"
      @input="emits('input')"
      @clear="emits('clear')"
      @blur="markDirty()"
    />
    <div class="absolute flex items-center top-8 right-2" v-bind="$attrs">
      <slot></slot>
    </div>
  </div>
</template>
